import type { components } from "@contracts/generated/editor-service";

import type { ScreeningDiagnosisDocument } from "./screeningDocuments";

export type ScreeningRuleProjection =
  components["schemas"]["ScreeningRuleProjection"];
export type ScreeningLanguageRules =
  components["schemas"]["ScreeningLanguageRules"];
export type ScreeningCriterion = components["schemas"]["ScreeningCriterion"];
export type ScreeningContextRefusal =
  components["schemas"]["ScreeningContextRefusal"];
export type RuleOrigin = components["schemas"]["RuleOrigin"];

/**
 * What a diagnosis rule DOES, recomputed live as the clinician edits it.
 *
 * ## Why this exists beside the server's own projection
 *
 * `GET /library/assets/{id}/screening-rules` already answers this question for a
 * STORED version, and `screening_semantics.py` is the authority. What it cannot
 * answer is the question the form is actually asking — *what will this do once I
 * save?* — because the draft changes on every keystroke and the answer must
 * change with it. A round trip per keystroke is not an option on a surface whose
 * whole job is to make an effect visible while it is being authored.
 *
 * So the split is: the SERVICE publishes the CONTEXT (the classification
 * criteria, base canon's own document, and every refusal that stopped either
 * from resolving), which is a fact about the version and the mounted pack and
 * does not change while typing; and this module applies the same derivation to
 * `context + draft`.
 *
 * ## What keeps the two from drifting
 *
 * `screeningSemantics.invariant.test.ts` replays a vector file the Python module
 * GENERATES (`tests/test_screening_semantics.py` fails when the committed file
 * is not what it produces), and asserts this module answers identically for
 * every case. The Python module is in turn held against the real
 * `diagnose_logic_process` at the pinned Brain
 * (`test_screening_semantics_against_real_brain.py`). So the chain is
 * browser ≡ service ≡ engine, and each link is checked in a lane that runs.
 *
 * This repository's rule about mirrors is that they are pinned to what they
 * mirror or deleted — `invariants/closingCompletionToken.test.ts` states it, and
 * `screeningStepRole.invariant.test.ts` is the same rule one file over. It lives
 * in `src/lib` for that file's reason: `pr-gate`'s `web-fast` job runs `src/lib`
 * as a DIRECTORY, so the pin is checked before a merge rather than after one.
 *
 * ## The engine rules, restated once
 *
 * Every one of these is `conversation_agent/diagnosis.py:diagnose_logic_process`:
 *
 *   * An absent `mandatory_roles` entry defaults to the criterion's ANSWER
 *     COUNT (`.get(matching_key, total_sub_steps)`) — every answer positive,
 *     never "no requirement".
 *   * `include_mandatory_yeses` is read twice. Forward, it decides whether a
 *     criterion's positive answers join the yes total (absent means yes).
 *     Backward, an explicit `true` additionally fails the whole screening when
 *     that criterion has zero positive answers.
 *   * Keys resolve by PREFIX and the two directions can land on different
 *     criteria.
 *   * A key that resolves to no criterion is INERT: stored, exported, and never
 *     read.
 */

/** Which layer supplies an effective value. Mirrors `RuleOrigin`. */
export const ORIGIN = {
  authored: "authored",
  inherited: "inherited",
  engineDefault: "engine_default",
  /**
   * A layer supplies a value this service cannot read as the type the engine
   * needs. Evidence that cannot be used, as against `unresolved`'s absence of
   * evidence — the pinned engine would RAISE on the document rather than decide.
   */
  unreadable: "unreadable",
  /**
   * No layer could answer: nothing authored, and the inherited document unread.
   *
   * The default is NOT the answer here. An omitted entry falls through to base
   * canon, so with that document unread the engine default is a guess — and one
   * that reads as a weaker rule, which is the failure this module exists to
   * remove.
   */
  unresolved: "unresolved",
} as const;

/** One criterion's effective threshold. */
export interface EffectiveThreshold {
  /** `null` when `origin` is `unresolved`. */
  readonly effective: number | null;
  readonly origin: RuleOrigin;
  readonly authored: number | null;
  readonly inherited: number | null;
  /** The base pack declares something here the runtime cannot use. */
  readonly inheritedUnreadable: boolean;
  /** The mapping key the runtime lands on, when it is not this criterion's own. */
  readonly resolvedKey: string | null;
}

/** One criterion's effective `include_mandatory_yeses` reading, both halves. */
export interface EffectiveInclusion {
  /** `null` when `origin` is `unresolved` or the mapping container is unreadable. */
  readonly countsTowardYesTotal: boolean | null;
  readonly requiresPositiveAnswer: boolean | null;
  readonly origin: RuleOrigin;
  readonly authored: boolean | null;
  readonly inherited: boolean | null;
  readonly resolvedKey: string | null;
}

/** The complete rule the runtime applies to one classification criterion. */
export interface EffectiveCriterion {
  readonly target: string;
  readonly answerCount: number;
  readonly threshold: EffectiveThreshold;
  readonly inclusion: EffectiveInclusion;
  /** No set of answers can make this criterion count — `999`'s consequence. */
  readonly unreachable: boolean;
}

/** One stored mapping entry, and the criteria (if any) the runtime applies it to. */
export interface EntryEffect {
  readonly field: DiagnosisMappingField;
  readonly key: string;
  readonly criteria: readonly string[];
  readonly hardCheckOn: string | null;
  /** The runtime reads this entry for no criterion at all. */
  readonly inert: boolean;
}

export type DiagnosisMappingField =
  | "mandatory_roles"
  | "include_mandatory_yeses";

/** The engine's read order: the inclusion flag decides the yes total first. */
export const MAPPING_FIELDS: readonly DiagnosisMappingField[] = [
  "mandatory_roles",
  "include_mandatory_yeses",
];

/**
 * The context one document runs in, as this module needs it.
 *
 * A narrowing of `ScreeningLanguageRules` rather than the wire type itself, so
 * a caller can build one in a test without a whole projection — and so the two
 * refusal fields cannot be confused: `criteria === null` means nothing was
 * resolved, while `criteriaPartial` means what WAS resolved may be missing
 * fields base canon declares.
 */
export interface ScreeningRuleContext {
  /** `stepN → answer count`, in the schema's own order, or null when unresolved. */
  readonly criteria: readonly (readonly [string, number])[] | null;
  readonly criteriaRefusal: ScreeningContextRefusal | null;
  readonly criteriaPartialRefusal: ScreeningContextRefusal | null;
  readonly inherited: ScreeningDiagnosisDocument | null;
  readonly inheritedRefusal: ScreeningContextRefusal | null;
}

/**
 * Whether "no layer declares this" is a conclusion or a guess.
 *
 * `not_supplied` is knowledge — base canon shipping nothing at this coordinate
 * is a resolved fact, and the engine's default is then the real answer. Every
 * other refusal means the document that WOULD have supplied the value was never
 * opened, and no effective value may be derived from its silence.
 */
export function inheritedIsKnown(context: ScreeningRuleContext): boolean {
  return (
    context.inheritedRefusal === null || context.inheritedRefusal === "not_supplied"
  );
}

/** The context of one language of a projection, or the unresolved context. */
export function contextForLanguage(
  projection: ScreeningRuleProjection | null | undefined,
  language: string | null,
): ScreeningRuleContext {
  const entry = projection?.languages.find(
    (candidate) => (candidate.language ?? null) === language,
  );
  if (entry === undefined) {
    // NOT AN EMPTY CONTEXT WITH NO REASON. A projection that has not been read,
    // or that carries no row for this language, is a context this browser cannot
    // describe — and `criteria: null` with no refusal beside it would render as
    // "still loading" forever. The caller distinguishes the two by whether it
    // has a projection at all.
    return {
      criteria: null,
      criteriaRefusal: null,
      criteriaPartialRefusal: null,
      inherited: null,
      inheritedRefusal: null,
    };
  }
  return {
    criteria:
      entry.criteria == null
        ? null
        : entry.criteria.map(
            (criterion) => [criterion.target, criterion.answerCount] as const,
          ),
    criteriaRefusal: entry.criteriaRefusal ?? null,
    criteriaPartialRefusal: entry.criteriaPartialRefusal ?? null,
    // READ DEFENSIVELY: the wire type is `JsonValue`, because these are stored
    // bytes from an immutable row and an externally authored pack tree. A shape
    // this cannot read contributes no inherited values rather than throwing.
    inherited: isRecord(entry.inheritedDocument)
      ? (entry.inheritedDocument as ScreeningDiagnosisDocument)
      : null,
    inheritedRefusal: entry.inheritedRefusal ?? null,
  };
}

/**
 * Whether "this target is not classifiable" is a conclusion or a guess.
 *
 * The browser's half of the write gate's own rule: an incomplete criteria list
 * cannot support an inertness claim, so the form must not paint one.
 */
export function criteriaAreComplete(context: ScreeningRuleContext): boolean {
  return context.criteria !== null && context.criteriaPartialRefusal === null;
}

/** `inherited ⊕ authored`, with the overlay merge's own combinators. */
export function mergeInherited(
  authored: ScreeningDiagnosisDocument | null | undefined,
  inherited: ScreeningDiagnosisDocument | null | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...(inherited ?? {}) };
  for (const [key, value] of Object.entries(authored ?? {})) {
    const base = merged[key];
    if (
      (MAPPING_FIELDS as readonly string[]).includes(key) &&
      isRecord(value) &&
      isRecord(base)
    ) {
      merged[key] = { ...base, ...value };
      continue;
    }
    merged[key] = value;
  }
  return merged;
}

/** `find_matching_key(step, mapping)` — the first key that STARTS WITH `step`. */
export function resolveForward(
  step: string,
  mapping: Record<string, unknown>,
): string | null {
  const needle = normalise(step);
  return (
    Object.keys(mapping).find((key) => normalise(key).startsWith(needle)) ?? null
  );
}

/** The reverse read: the first criterion whose name STARTS WITH `key`. */
export function resolveReverse(
  key: string,
  steps: readonly string[],
): string | null {
  const needle = normalise(key);
  return steps.find((step) => normalise(step).startsWith(needle)) ?? null;
}

function normalise(name: string): string {
  return name.toLowerCase().replaceAll(" ", "");
}

/**
 * Every classification criterion with its effective rule, or `null` when the
 * criteria could not be resolved.
 *
 * `null` is never approximated by an empty list: an empty list is the real
 * answer for a classification schema that declares no fields.
 */
export function effectiveCriteria(
  authored: ScreeningDiagnosisDocument | null | undefined,
  context: ScreeningRuleContext,
): EffectiveCriterion[] | null {
  if (context.criteria === null) {
    return null;
  }
  const effective = mergeInherited(authored, context.inherited);
  const steps = context.criteria.map(([name]) => name);
  return context.criteria.map(([target, answerCount]) => {
    const threshold = thresholdFor(target, answerCount, authored, context, effective);
    return {
      target,
      answerCount,
      threshold,
      inclusion: inclusionFor(target, steps, authored, context, effective),
      // An unresolved threshold proves nothing about reachability.
      unreachable:
        threshold.effective !== null && threshold.effective > answerCount,
    };
  });
}

function thresholdFor(
  target: string,
  answerCount: number,
  authored: ScreeningDiagnosisDocument | null | undefined,
  context: ScreeningRuleContext,
  effective: Record<string, unknown>,
): EffectiveThreshold {
  const known = inheritedIsKnown(context);
  if (mappingIsUnreadable(effective, "mandatory_roles")) {
    return {
      effective: null,
      origin: ORIGIN.unreadable,
      authored: null,
      inherited: null,
      inheritedUnreadable: mappingIsUnreadable(
        context.inherited,
        "mandatory_roles",
      ),
      resolvedKey: null,
    };
  }
  const merged = mappingOf(effective, "mandatory_roles");
  const key = resolveForward(target, merged);
  const authoredMap = mappingOf(authored, "mandatory_roles");
  const inheritedMap = mappingOf(context.inherited, "mandatory_roles");
  if (key === null) {
    // THE ENGINE'S DEFAULT IS EVERY ANSWER, not none — and it is only the answer
    // once the inherited document has been read, or positively shown absent.
    return {
      effective: known ? answerCount : null,
      origin: known ? ORIGIN.engineDefault : ORIGIN.unresolved,
      authored: null,
      inherited: null,
      inheritedUnreadable: false,
      resolvedKey: null,
    };
  }
  if (!forwardIsCertain(key, known)) {
    return {
      effective: null,
      origin: ORIGIN.unresolved,
      authored: null,
      inherited: null,
      inheritedUnreadable: false,
      resolvedKey: null,
    };
  }
  // `effective` is this function's own parameter (the merged document), so the
  // resolved value takes a different name rather than shadowing it.
  const resolved = asCount(merged[key]);
  const inheritedPresent = key in inheritedMap;
  const inheritedValue = inheritedPresent ? asCount(inheritedMap[key]) : null;
  // PRESENCE AND USABILITY ARE TWO FACTS. Normalising an unusable inherited
  // value to `null` says "the base pack sets nothing", and the form then
  // promises that clearing this control falls to the engine's default — when it
  // actually restores a value the engine RAISES on.
  const inheritedUnreadable = inheritedPresent && inheritedValue === null;
  if (resolved === null) {
    return {
      effective: null,
      origin: ORIGIN.unreadable,
      authored: null,
      inherited: inheritedValue,
      inheritedUnreadable,
      resolvedKey: null,
    };
  }
  return {
    effective: resolved,
    origin: key in authoredMap ? ORIGIN.authored : ORIGIN.inherited,
    authored: asCount(authoredMap[key]),
    inherited: inheritedValue,
    inheritedUnreadable,
    resolvedKey: key === target ? null : key,
  };
}

function inclusionFor(
  target: string,
  steps: readonly string[],
  authored: ScreeningDiagnosisDocument | null | undefined,
  context: ScreeningRuleContext,
  effective: Record<string, unknown>,
): EffectiveInclusion {
  const known = inheritedIsKnown(context);
  if (mappingIsUnreadable(effective, "include_mandatory_yeses")) {
    return {
      countsTowardYesTotal: null,
      requiresPositiveAnswer: null,
      origin: ORIGIN.unreadable,
      authored: null,
      inherited: null,
      resolvedKey: null,
    };
  }
  const merged = mappingOf(effective, "include_mandatory_yeses");
  const authoredMap = mappingOf(authored, "include_mandatory_yeses");
  const inheritedMap = mappingOf(context.inherited, "include_mandatory_yeses");
  const forward = resolveForward(target, merged);
  // THE REVERSE PASS IS A SEPARATE RESOLUTION and can land on another key —
  // which is why it is computed rather than read off the forward answer.
  const hard = Object.entries(merged).some(
    ([key, value]) => pythonTruthy(value) && resolveReverse(key, steps) === target,
  );
  if (forward === null) {
    return {
      countsTowardYesTotal: known ? true : null,
      requiresPositiveAnswer: known ? hard : hard ? true : null,
      origin: known ? ORIGIN.engineDefault : ORIGIN.unresolved,
      authored: null,
      inherited: null,
      resolvedKey: null,
    };
  }
  // A hard check FOUND in the layers that were read is a fact whatever else is
  // unread; its ABSENCE is not, so that half goes unresolved instead.
  const requiresPositiveAnswer = known || hard ? hard : null;
  if (!forwardIsCertain(forward, known)) {
    return {
      countsTowardYesTotal: null,
      requiresPositiveAnswer,
      origin: ORIGIN.unresolved,
      authored: null,
      inherited: null,
      resolvedKey: null,
    };
  }
  // Individual values are always readable through truthiness. The mapping
  // container itself was checked above because Brain calls `.items()` first.
  return {
    countsTowardYesTotal: asFlag(merged[forward]),
    requiresPositiveAnswer,
    origin: forward in authoredMap ? ORIGIN.authored : ORIGIN.inherited,
    authored: forward in authoredMap ? asFlag(authoredMap[forward]) : null,
    inherited: forward in inheritedMap ? asFlag(inheritedMap[forward]) : null,
    resolvedKey: forward === target ? null : forward,
  };
}

/**
 * Every mapping entry of the draft, with the criteria the runtime applies it to.
 *
 * `null` when the criteria are unresolved: inertness is a claim ABOUT the
 * classifier, and there is nothing to make it against.
 */
export function entryEffects(
  authored: ScreeningDiagnosisDocument | null | undefined,
  context: ScreeningRuleContext,
): EntryEffect[] | null {
  if (context.criteria === null) {
    return null;
  }
  const steps = context.criteria.map(([name]) => name);
  const effects: EntryEffect[] = [];
  for (const field of MAPPING_FIELDS) {
    const mapping = mappingOf(authored, field);
    const applied = new Map<string, string[]>(
      Object.keys(mapping).map((key) => [key, []]),
    );
    for (const step of steps) {
      const landed = resolveForward(step, mapping);
      if (landed !== null) {
        applied.get(landed)?.push(step);
      }
    }
    for (const key of Object.keys(mapping)) {
      const hardCheckOn =
        field === "include_mandatory_yeses" && pythonTruthy(mapping[key])
          ? resolveReverse(key, steps)
          : null;
      const criteria = applied.get(key) ?? [];
      effects.push({
        field,
        key,
        criteria,
        hardCheckOn,
        inert: criteria.length === 0 && hardCheckOn === null,
      });
    }
  }
  return effects;
}

/**
 * The document-level count, with the layer supplying it.
 *
 * `yes_count` has no engine default — the pinned loader INDEXES it, so an
 * effective document without it raises mid-conversation. Pass `engineDefault:
 * null` for it, and the `engine_default` origin beside a null value is that
 * state rather than a zero this browser invented.
 */
export function effectiveCount(
  field: "yes_count" | "mandatory_count",
  authored: ScreeningDiagnosisDocument | null | undefined,
  context: ScreeningRuleContext,
  engineDefault: number | null,
): {
  readonly effective: number | null;
  readonly origin: RuleOrigin;
  readonly inherited: number | null;
  readonly inheritedUnreadable: boolean;
} {
  // A SCALAR, SO THE OVERLAY REPLACES IT OUTRIGHT — no prefix resolution and no
  // ordering, which is why an authored count IS certain with the base unread
  // while an authored mapping entry is not.
  const inheritedRecord = context.inherited as Record<string, unknown> | null;
  const inheritedPresent = inheritedRecord != null && field in inheritedRecord;
  const inherited = inheritedPresent ? asCount(inheritedRecord[field]) : null;
  const inheritedUnreadable = inheritedPresent && inherited === null;
  if (authored != null && field in authored) {
    const value = asCount((authored as Record<string, unknown>)[field]);
    if (value === null) {
      return {
        effective: null,
        origin: ORIGIN.unreadable,
        inherited,
        inheritedUnreadable,
      };
    }
    return {
      effective: value,
      origin: ORIGIN.authored,
      inherited,
      inheritedUnreadable,
    };
  }
  if (inheritedPresent) {
    if (inherited === null) {
      return {
        effective: null,
        origin: ORIGIN.unreadable,
        inherited: null,
        inheritedUnreadable: true,
      };
    }
    return {
      effective: inherited,
      origin: ORIGIN.inherited,
      inherited,
      inheritedUnreadable: false,
    };
  }
  if (!inheritedIsKnown(context)) {
    return {
      effective: null,
      origin: ORIGIN.unresolved,
      inherited: null,
      inheritedUnreadable: false,
    };
  }
  return {
    effective: engineDefault,
    origin: ORIGIN.engineDefault,
    inherited,
    inheritedUnreadable: false,
  };
}

function mappingOf(
  document: ScreeningDiagnosisDocument | Record<string, unknown> | null | undefined,
  field: DiagnosisMappingField,
): Record<string, unknown> {
  const value = (document as Record<string, unknown> | null | undefined)?.[field];
  return isRecord(value) ? value : {};
}

function mappingIsUnreadable(
  document: ScreeningDiagnosisDocument | Record<string, unknown> | null | undefined,
  field: DiagnosisMappingField,
): boolean {
  if (!isRecord(document) || !(field in document)) {
    return false;
  }
  return !isRecord(document[field]);
}

/**
 * PYTHON'S truthiness, which is the engine's — not JavaScript's.
 *
 * `diagnose_logic_process` tests `if json_key and required`, so the falsy set is
 * Python's: `false`, `0`, `""`, `[]`, `{}` and `null`. JavaScript disagrees on
 * two of those — `[]` and `{}` are truthy here — and a stored
 * `include_mandatory_yeses: {step2: []}` is exactly the shape an immutable
 * pre-schema row can hold. `Boolean(value)` therefore reported a hard per-step
 * check the runtime does not apply, which the shared vector file caught: the
 * browser said `requiresPositiveAnswer: true` where the service said `false`.
 */
function pythonTruthy(value: unknown): boolean {
  if (value === null || value === undefined || value === false) {
    return false;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    return value.length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  return Boolean(value);
}

/**
 * A threshold as the RUNTIME uses it, or `null` when the runtime RAISES.
 *
 * THE PINNED ENGINE'S BEHAVIOUR, NOT THIS SERVICE'S SCHEMA. `step_yes >= value`
 * decides for `-1`, `0`, `0.5` and `true` — probed at the pin — and raises only
 * for a string, a list, an object or null. The write gate still refuses those
 * shapes on new content; what this reads is what an immutable row already holds,
 * and calling a usable value unreadable told a clinician the engine would fail
 * on a document it decides with.
 */
function asCount(value: unknown): number | null {
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * An inclusion flag as the RUNTIME reads it. NEVER null.
 *
 * Both reads are bare truthiness, so every stored value has an answer and none
 * raises — `[]` is false and the string `"false"` is TRUE.
 */
function asFlag(value: unknown): boolean {
  return pythonTruthy(value);
}

/**
 * Whether the forward resolution for `step` is a fact or a guess.
 *
 * THE MERGE PUTS INHERITED KEYS FIRST — `mergeInherited` spreads the inherited
 * mapping and then the authored one, so a key only the overlay declares is
 * appended while the engine takes the FIRST prefix match. An unread inherited
 * mapping may hold `step10` ahead of an authored `step1`, and the engine would
 * read that one. Verified against the pin: merged `{step10: 9, step1: 2}`
 * answers False where the authored mapping alone answers True.
 */
function forwardIsCertain(
  authoredKey: string | null,
  known: boolean,
): boolean {
  return known || authoredKey === null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
